import { AfterViewInit, ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { combineLatest, takeUntil } from 'rxjs';
import { BaseComponent } from 'src/app/core/components/base/base.component';
import { FeedbackService } from './../../../core/services/feedback.service';
import { LogbookService } from './../../../core/services/logbook.service';
import { MorbidityMortalityService } from './../../../core/services/morbidity-mortality.service';
import { RiskItemService } from './../../../core/services/risk-item.service';
import { StateCacheService } from './../../../core/services/state-cache.service';
import { TagService } from './../../../core/services/tag.service';
import { TaskService } from './../../../core/services/task.service';
import { UserService } from './../../../core/services/user.service';
import { isTaskForUser } from './../../../core/utils/task-assignees';
import { RiskManagementListComponent } from './risk-management-list/risk-management-list.component';
import { OPEN_RISK_STATUSES } from './../../../shared/components/risk-suggestion-list/risk-suggestion.model';

@Component({
  selector: 'app-patient-filters-list',
  templateUrl: './patient-filters-list.component.html',
  styleUrls: ['./patient-filters-list.component.scss']
})
export class PatientFiltersListComponent extends BaseComponent implements OnInit, AfterViewInit, OnDestroy {
  filterValue: string = '';
  selectedTabId: number = 0;
  /**
   * Index of the Risk Management tab. FOURTH of five, after Logbook (2026-09-01).
   *
   * Must track the template. The toolbar actions are gated on this, so a stale value does not
   * fail loudly — it silently shows the register export while a different tab is open, or
   * hides it on the right one. Move the tab, change this.
   */
  private static readonly RISK_TAB_INDEX = 3;

  /**
   * The register, so the toolbar can trigger ITS menus and read ITS filter state.
   *
   * Every tab body renders eagerly (plain `<div>`, not `matTabContent`), so this resolves once
   * the view initialises regardless of which tab is showing.
   */
  @ViewChild(RiskManagementListComponent) riskList?: RiskManagementListComponent;

  get isRiskTabActive(): boolean {
    return this.selectedTabId === PatientFiltersListComponent.RISK_TAB_INDEX;
  }


  totalLogbook?: number;
  totalMorbidityMortality?: number;
  totalTasks?: number;
  totalTags?: number;
  /**
   * G3. Counts items that still need attention — `Open` AND `Reported` (D9).
   *
   * `Reported` is NOT finished: it means the issue has gone to the facility and nobody has come
   * back yet. Counting only `Open` made the badge drop to zero the moment a register was
   * exported, which reads as "the register emptied" when in fact every row had just been sent.
   *
   * `Resolved` and `Dismissed` are the terminal states and are excluded, so the badge still
   * means "outstanding" rather than "ever created" — a badge that only grew would stop being
   * read within a month. Same intent as `totalTasks` above, which counts `Pending`.
   */
  totalRiskItems?: number;

  constructor(
    private stateCache: StateCacheService,
    private logbookService: LogbookService,
    private morbidityMortalityService: MorbidityMortalityService,
    private taskService: TaskService,
    private userService: UserService,
    private tagService: TagService,
    private riskItemService: RiskItemService,
    private cdr: ChangeDetectorRef,
    fs: FeedbackService,
    md: MatDialog
  ) {
    super(fs, md);

    this.logbookService.logbook$
      .pipe(takeUntil(this.destroy$))
      .subscribe((results: any) => {
        this.totalLogbook = results?.length || 0;
      });

    this.morbidityMortalityService.morbidityMortality$
      .pipe(takeUntil(this.destroy$))
      .subscribe((results: any) => {
        this.totalMorbidityMortality = results?.length || 0;
      });

    // "Mine": assigned to nobody (the whole practice) or to me — the Task List tab's default view, and the
    // same rule as the dashboard popup. `tasks/task-assignment.md`.
    combineLatest([this.taskService.tasks$, this.userService.userProfile$])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([tasks, user]: [any, any]) => {
        this.totalTasks = tasks?.length
          ? tasks.filter((task: any) => task.status === 'Pending' && isTaskForUser(task, user?.id)).length
          : 0;
      });

    this.tagService.taggedPatients$
      .pipe(takeUntil(this.destroy$))
      .subscribe((results: any) => {
        this.totalTags = results?.data?.length || 0;
      });

    this.riskItemService.riskItems$
      .pipe(takeUntil(this.destroy$))
      .subscribe((items: any) => {
        this.totalRiskItems = items?.length ? items.filter((item: any) => OPEN_RISK_STATUSES.includes(item.status)).length : 0;
      });


  }

  /**
   * `riskList` is populated during view init, and the toolbar above reads it in the SAME change
   * detection pass. Without this the first render throws
   * `ExpressionChangedAfterItHasBeenCheckedError` — the guard evaluates `undefined`, the child
   * resolves, and the value has changed by the time Angular re-checks.
   */
  ngAfterViewInit(): void {
    this.cdr.detectChanges();
  }

  ngOnInit(): void {
    const currentTab = this.stateCache.get<number>('filtersTab')! || 0;
    this.selectedTabId = currentTab;
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.stateCache.set('filtersTab', this.selectedTabId);
  }

  onTabChange(index: number) {
    this.selectedTabId = index;
    this.stateCache.set('filtersTab', index);
  }

  applyFilter(event: Event) {
    this.filterValue = (event.target as HTMLInputElement).value.toLowerCase();
  }




}